import gql from 'graphql-tag'
import graphql from 'utils/graphql'
import {
    InviteTo,
    SendTestEmailMutation,
    SendTestEmailMutationVariables,
    SendInviteEmailsMutation,
    SendInviteEmailsMutationVariables,
    CreateInvite,
    CreateInviteMutation,
    CreateInviteMutationVariables,
    UpdateInvite,
    UpdateInviteMutation,
    UpdateInviteMutationVariables,
    SetIsStaffMutation,
    SetIsStaffMutationVariables,
    ActiveEventMutation,
    ActiveEventMutationVariables,
    ImportAttendeesMutation,
    ImportAttendeesMutationVariables,
    QueueBatchInviteEmails,
    MutationQueueBatchInviteEmailsArgs,
    CreateTagCategory,
    CreateProgramTagCategoryMutation,
    CreateProgramTagCategoryMutationVariables,
    CreateTag,
    CreateProgramTagMutation,
    CreateProgramTagMutationVariables,
    UpdateProgramTagMutation,
    MutationUpdateAttendeeTagArgs,
    DeleteProgramTagMutation,
    DeleteProgramTagMutationVariables,
    DeleteProgramTagCategoryMutation,
    DeleteProgramTagCategoryMutationVariables,
    TagType,
    MutationUpdateProgramTagArgs,
    MutationUpdateProgramTagCategoryArgs
} from 'generated/graphql'

const sendTestEmailMutation = gql`
    mutation sendTestEmail($eventSlug: EventID!) {
        sendTestEmailToCurrentUser(eventId: $eventSlug)
    }
`
export const sendTestEmail = (eventSlug) => graphql.createQuery<
    SendTestEmailMutation, SendTestEmailMutationVariables
>(sendTestEmailMutation)({ eventSlug })

const sendInviteEmailsMutation = gql`
    mutation sendInviteEmails($data: SendInviteEmails!) {
        sendInviteEmails(data: $data) {
            email
            status
            mailSent
        }
    }
`
export const sendInviteEmails = (eventSlug, to: number[]) => {
    const mutation = graphql.createQuery<
        SendInviteEmailsMutation, SendInviteEmailsMutationVariables
    >(sendInviteEmailsMutation)
    return mutation({ data: { eventId: eventSlug, to } })
}

const sendBatchEmailsMutation = gql`
    mutation sendBatchEmails($data: QueueBatchInviteEmails!) {
        queueBatchInviteEmails(data: $data)
    }
`
export const sendBatchEmails = (eventSlug, to: InviteTo) => {
    const mutation = graphql.createQuery<
        QueueBatchInviteEmails, MutationQueueBatchInviteEmailsArgs
    >(sendBatchEmailsMutation)
    return mutation({ data: { eventId: eventSlug, to } })
}

const createInviteMutation = gql`
    mutation createInvite($data: CreateInvite!) {
        createInvite(data: $data) {
            id
        }
    }
`
export const createInvite = (data: CreateInvite) => graphql.createQuery<
    CreateInviteMutation, CreateInviteMutationVariables
>(createInviteMutation)({ data })

const updateInviteMutation = gql`
    mutation updateInvite($id: ID!, $data: UpdateInvite!) {
        updateInvite(id: $id, data: $data) {
            id
        }
    }
`
export const updateInvite = (id, data: UpdateInvite) => graphql.createQuery<
    UpdateInviteMutation, UpdateInviteMutationVariables
>(updateInviteMutation)({ id, data })

const deleteInviteMutation = gql`
    mutation deleteInvite($id: ID!) {
        deleteInvite(id: $id)
    }
`
export const deleteInvite = (id) => graphql.createQuery(deleteInviteMutation)({ id })

const setIsStaffMutation = gql`
    mutation setIsStaff($data: SetIsStaff!) {
        setIsStaff(data: $data)
    }
`
export const setIsStaff = (eventSlug, userEmail, isStaff) => graphql.createQuery<
    SetIsStaffMutation, SetIsStaffMutationVariables
>(setIsStaffMutation)({ data: { eventId: eventSlug, userEmail, isStaff } })

const activeEventMutation = gql`
    mutation activeEvent($eventSlug: EventID!) {
        requestEventActivation(eventId: $eventSlug) {
            id
            isActivationRequested
        }
    }
`
export const activeEvent = (eventSlug) => graphql.createQuery<
    ActiveEventMutation, ActiveEventMutationVariables
>(activeEventMutation)({ eventSlug })

const importAttendeesMutation = gql`
    mutation importAttendees($eventSlug: EventID!, $data: [ImportInvite!]!) {
        importInvites(event: $eventSlug, data: $data) {
            successCount
            errorCount
            errorItems {
                id
                message
                hiddenMessage
            }
        }
    }
`
export const importAttendees = ({ eventSlug, data }: ImportAttendeesMutationVariables) => (
    graphql.createQuery<
        ImportAttendeesMutation, ImportAttendeesMutationVariables
    >(importAttendeesMutation)({ eventSlug, data })
)

const createAttendeeTagCategory = gql`
    mutation createAttendeeTagCategory($data: CreateTagCategory!) {
        createAttendeeTagCategory(data: $data) {
            id
        }
    }
`

const createAttendeeTagMutation = gql`
    mutation createAttendeeTag($data: CreateTag!) {
        createAttendeeTag(data: $data) {
            id
        }
    }
`
const updateAttendeeTagMutation = gql`
    mutation updateAttendeeTag($data: UpdateTag!, $id: ID!) {
        updateAttendeeTag(data: $data, id: $id) {
            id
        }
    }
`

const deleteAttendeeTagMutation = gql`
    mutation deleteAttendeeTag($id: ID!) {
        deleteAttendeeTag(id: $id)
    }
`

const updateAttendeeTagCategoryMutation = gql`
    mutation updateAttendeeTagCategory($data: UpdateTagCategory!, $id: ID!) {
        updateAttendeeTagCategory(data: $data, id: $id) {
            id
        }
    }
`

const deleteAttendeeTagCategoryMutation = gql`
    mutation deleteAttendeeTagCategory($id: ID!) {
        deleteAttendeeTagCategory(id: $id)
    }
`

export const createAttendeeCategory = (data: CreateTagCategory) => {
    const query = graphql.createQuery<
        CreateProgramTagCategoryMutation, CreateProgramTagCategoryMutationVariables
        >(createAttendeeTagCategory)
    return query({ data })
}

export const createAttendeeTag = (data: CreateTag) => {
    const query = graphql.createQuery<
        CreateProgramTagMutation, CreateProgramTagMutationVariables
        >(createAttendeeTagMutation)
    return query({ data })
}

export const updateAttendeeTag = ({ text, color, categoryId }, id) => {
    const query = graphql.createQuery<
        UpdateProgramTagMutation, MutationUpdateAttendeeTagArgs
        >(updateAttendeeTagMutation)
    return query({ data: { text, color, categoryId }, id })
}

export const deleteAttendeeTag = (id) => {
    const query = graphql.createQuery<
        DeleteProgramTagMutation, DeleteProgramTagMutationVariables
        >(deleteAttendeeTagMutation)
    return query({ id })
}

export const deleteAttendeeCategory = (id) => {
    const query = graphql.createQuery<
        DeleteProgramTagCategoryMutation, DeleteProgramTagCategoryMutationVariables
        >(deleteAttendeeTagCategoryMutation)
    return query({ id })
}

export const updateAttendeeTagOrder = (id, order, categoryId) => {
    const query = graphql.createQuery<TagType, MutationUpdateProgramTagArgs>(updateAttendeeTagMutation)
    return query({ data: { order, categoryId: categoryId || null }, id })
}

export const updateAttendeeCategoryOrder = (id, order) => {
    const query = graphql.createQuery<
        UpdateProgramTagMutation, MutationUpdateProgramTagCategoryArgs>(updateAttendeeTagCategoryMutation)
    return query({ data: { order }, id })
}

export const updateAttendeeCategory = (id, text) => {
    const query = graphql.createQuery<
        UpdateProgramTagMutation, MutationUpdateProgramTagCategoryArgs>(updateAttendeeTagCategoryMutation)
    return query({ data: { text }, id })
}
